import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { 
  FileText, 
  MessageSquare, 
  Users, 
  Globe, 
  Shield,
  ArrowRight,
  Phone
} from "lucide-react";

const HeroSection = () => {
  const navigate = useNavigate();

  const trustPoints = [
    { icon: Shield, text: "Best CSC Billing Software for Indian VLEs", emoji: "🇮🇳" },
    { icon: FileText, text: "Free Billing Software with GST Invoice", emoji: "🧾" },
    { icon: MessageSquare, text: "WhatsApp Invoice & Payment Reminder", emoji: "📲" },
    { icon: Users, text: "Kiosk Banking, AEPS, PMFBY CSC, Pik Vima", emoji: "🏦" },
    { icon: Globe, text: "Maha e-Seva Kendra | Hindi | Marathi | English", emoji: "🌐" },
  ];

  return (
    <section className="relative min-h-screen bg-gradient-to-br from-[hsl(220,60%,15%)] via-[hsl(220,55%,20%)] to-[hsl(220,50%,25%)] text-white overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-72 h-72 bg-green-400 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-400 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 py-16 md:py-24 relative z-10">
        <div className="max-w-5xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-green-500/20 border border-green-400/30 rounded-full px-4 py-2 mb-8">
            <span className="text-green-400 font-medium text-sm">🚀 #1 CSC Billing Software | Free for Maha e-Seva Kendra</span>
          </div>

          {/* H1 - SEO Optimized for target keywords */}
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
            <span className="text-green-400">VLE Plus</span> – Best{" "}
            <span className="text-green-400">CSC Billing Software</span> for{" "}
            Maha e-Seva Kendra & VLE
          </h1>

          {/* Subheading with keywords - min-height prevents CLS */}
          <p className="text-lg md:text-xl text-gray-300 mb-10 max-w-3xl mx-auto leading-relaxed min-h-[4.5rem] md:min-h-[3.5rem]">
            Free billing software for <span className="text-white font-medium inline-block">CSC VLE, Maha e-Seva Kendra</span> & Digital Seva operators.{" "}
            <span className="text-white font-medium inline-block">
              Kiosk Banking, AEPS, PMFBY CSC, Pik Vima, WhatsApp Invoice & VLE Referral Program
            </span>
          </p>

          {/* Trust Points */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10 max-w-4xl mx-auto">
            {trustPoints.map((point, index) => (
              <div 
                key={index}
                className="flex items-center gap-3 bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg px-4 py-3 text-left"
              >
                <span className="text-xl">{point.emoji}</span>
                <span className="text-sm text-white">{point.text}</span>
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button 
              size="lg"
              onClick={() => navigate("/auth")}
              className="bg-green-700 hover:bg-green-800 text-white font-semibold px-8 py-6 text-lg shadow-lg shadow-green-700/30 transition-all hover:shadow-xl hover:shadow-green-700/40"
            >
              Start Free – Login to Dashboard
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-white text-white bg-white/10 hover:bg-white/30 hover:border-white px-8 py-6 text-lg transition-all"
            >
              <Phone className="mr-2 h-5 w-5" />
              Book Free Demo
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-6 mt-16 max-w-2xl mx-auto">
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-white">1000+</div>
              <div className="text-sm text-gray-300 mt-1">Active VLEs</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-white">50K+</div>
              <div className="text-sm text-gray-300 mt-1">Invoices Created</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-white">₹5Cr+</div>
              <div className="text-sm text-gray-300 mt-1">Transactions</div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="hsl(0, 0%, 100%)"/>
        </svg>
      </div>
    </section>
  );
};

export default HeroSection;
